import { Chip } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useFormikContext } from 'formik';
import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { apiClient } from '../../../../../../../../../api';
import { GipReportValues } from '../types';
import { StyledAutocompleteField } from './StyledAutocompleteField';

export const AdvertisersFieldMui = () => {
  const { $t } = useIntl();
  const { values, setFieldValue, setFieldTouched, isSubmitting } =
    useFormikContext<GipReportValues>();
  const [searchTerm, setSearchTerm] = useState('');

  const queryEnabled = searchTerm.length >= 2;
  const params = {
    from: values.from,
    to: values.to,
    stations: values.stations?.map((s) => s.id) || [],
    search: searchTerm,
  };

  const query = useQuery({
    queryKey: ['gip-report-advertisers-search', params],
    queryFn: () =>
      apiClient.audit.gipFetchAdvertisers(params).then((values) => values.filter((v) => !!v)),
    enabled: queryEnabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const options = useMemo(() => {
    return query.data?.map((name) => ({ name })) || [];
  }, [query.data]);

  const selectedValues = useMemo(() => {
    return values.advertisers || [];
  }, [values.advertisers]);

  const handleChange = (event: any, newValue: { name: string }[]) => {
    setFieldValue('advertisers', newValue);
    setFieldTouched('advertisers');
  };

  const handleInputChange = (event: any, newInputValue: string) => {
    setSearchTerm(newInputValue);
  };

  return (
    <StyledAutocompleteField
      name="advertisers"
      label={$t({
        id: 'audit.gipReport.form.advertisers',
        defaultMessage: 'Anunciantes',
      })}
      placeholder={$t({
        id: 'audit.gipReport.form.allAdvertisers',
        defaultMessage: 'Todos los anunciantes',
      })}
      helperText={
        searchTerm.length > 0 && searchTerm.length < 2
          ? $t({
              id: 'audit.gipReport.form.typeToSearch',
              defaultMessage: 'Ingrese al menos 2 caracteres iniciales',
            })
          : undefined
      }
      multiple
      options={options}
      value={selectedValues}
      onChange={handleChange}
      onInputChange={handleInputChange}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, value) => option.name === value.name}
      loading={query.isFetching}
      readOnly={isSubmitting}
      loadingText={$t({
        id: 'common.loading',
        defaultMessage: 'Cargando...',
      })}
      noOptionsText={
        searchTerm.length < 2
          ? $t({
              id: 'audit.gipReport.form.typeToSearch',
              defaultMessage: 'Ingrese al menos 2 caracteres iniciales',
            })
          : $t({
              id: 'common.noOptions',
              defaultMessage: 'No se encontraron resultados',
            })
      }
      renderTags={(value, getTagProps) =>
        value.map((option, index) => (
          <Chip label={option.name} {...getTagProps({ index })} key={option.name} size="small" />
        ))
      }
      filterOptions={(options) => options} // Disable client-side filtering since we're doing server-side search
      freeSolo={false}
    />
  );
};
